import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';
import loadImage from '../assets/imageLoader';
import { FaStar, FaUser, FaRegFrownOpen } from 'react-icons/fa';

const WorkerRatingsPage = () => {
    const { user } = useAuth();
    const [ratings, setRatings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ avg: 0, total: 0 });

    useEffect(() => {
        const fetchRatings = async () => {
            try {
                setLoading(true);
                // Use the profile ID to fetch ratings
                const profileId = user.profileId;
                const res = await apiClient.get(`/profiles/${profileId}/ratings`);
                const data = res.data || [];
                setRatings(data);

                if (data.length > 0) {
                    const avg = data.reduce((acc, r) => acc + r.rating, 0) / data.length;
                    setStats({ avg: avg.toFixed(1), total: data.length });
                }
            } catch (err) {
                console.error("Failed to fetch ratings", err);
            } finally {
                setLoading(false);
            }
        };
        if (user?.profileId) fetchRatings();
    }, [user]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20 bg-white rounded-[2rem] border border-slate-100 animate-pulse">
            <div className="w-16 h-16 bg-slate-100 rounded-full mb-4"></div>
            <div className="h-4 bg-slate-100 w-48 rounded"></div>
        </div>
    );

    return (
        <div className="space-y-8 font-sans">
            {/* Header / Stats */}
            <div className="bg-slate-900 text-white p-8 md:p-12 rounded-[2.5rem] shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black mb-2 tracking-tight">Worker Reputation</h1>
                        <p className="text-slate-400 font-medium">Your track record of excellence as rated by employers.</p>
                    </div>
                    
                    <div className="flex items-center gap-6 bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/10">
                        <div className="text-center">
                            <p className="text-[10px] font-black uppercase text-emerald-400 tracking-widest mb-1">Average Score</p>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-4xl font-black">{stats.avg}</span>
                                <FaStar className="text-yellow-400" />
                            </div>
                        </div>
                        <div className="w-px h-12 bg-white/10"></div>
                        <div className="text-center">
                            <p className="text-[10px] font-black uppercase text-emerald-400 tracking-widest mb-1">Total Reviews</p>
                            <p className="text-4xl font-black">{stats.total}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Ratings List */}
            <div className="space-y-4 max-w-4xl mx-auto">
                {ratings.length > 0 ? (
                    ratings.map((rating) => (
                        <div key={rating.id} className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-all group">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <img 
                                            src={loadImage(rating.rater?.picture)} 
                                            className="w-14 h-14 rounded-2xl object-cover border-2 border-slate-50 shadow-sm" 
                                            alt="Rater" 
                                        />
                                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center text-[8px] text-white">
                                            <FaUser />
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-black text-slate-800">{rating.rater?.name || 'Verified Customer'}</h4>
                                        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">{new Date(rating.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                    </div>
                                </div>
                                
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <FaStar 
                                            key={star} 
                                            className={`text-lg ${star <= rating.rating ? 'text-yellow-400 drop-shadow-sm' : 'text-slate-100'}`} 
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-50 relative">
                                <div className="absolute top-0 left-6 -translate-y-1/2 w-4 h-4 bg-slate-50 border-l border-t border-slate-50 rotate-45"></div>
                                <p className="text-slate-600 font-medium leading-relaxed italic text-base">
                                    "{rating.review || 'The employer did not leave a written comment, but gave you a stellar rating.'}"
                                </p>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="bg-white p-20 rounded-[3rem] text-center border border-slate-100 shadow-sm">
                        <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-200">
                            <FaRegFrownOpen size={48} />
                        </div>
                        <h3 className="text-2xl font-black text-slate-800">No Ratings Yet</h3>
                        <p className="text-slate-500 font-medium max-w-md mx-auto mt-2 leading-relaxed">
                            Finish more jobs to build your reputation. Verified ratings from employers will appear here.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkerRatingsPage;
